/*
 * MultipartUploader — режет File на части, отправляет PUT через BFF
 * с проверкой доступа перед каждой частью и финализирует через бэк. Поддерживает resume через
 * localStorage (по recording_id).
 *
 * Параллелизм: 3 одновременных PUT (см. ResumeState — `inflight`).
 *
 * Прогресс: на каждый загруженный байт через XMLHttpRequest.upload.onprogress.
 * Fetch + ReadableStream работают на progress только в Chrome 105+; XHR
 * совместим со всеми браузерами, оставшимися в проде на 2026 год.
 */
import {
  abortUpload,
  completeUpload,
  initUpload,
  type UploadCompletedPart,
  type UploadInitResponse,
} from "../api/uploads";

import { apiURL, csrfHeaders, processAuthError, uploadActivity } from "../auth/session";
import type { RecordingVisibility } from "../types";
import { linkTag } from "../api/tags";

const PARALLELISM = 3;

/** Состояние одного upload'а, сохраняемое в localStorage для resume. */
export interface ResumeState {
  recording_id: string;
  filename: string;
  size_bytes: number;
  part_size: number;
  /** Последний modificationTime файла (File.lastModified) — для sanity-check'а
   *  что юзер при resume выбрал тот же файл, а не похожий с тем же именем. */
  file_last_modified: number;
  /** Уже залитые части. Хранятся в порядке возрастания part_number. */
  completed_parts: UploadCompletedPart[];
  /** ms timestamp создания, для UX "загружено N часов назад". */
  started_at: number;
  pending_tags?: { id: string; version: number }[];
}

const STORAGE_PREFIX = "upload:";

/** Сохраняем state атомарно — на каждый успешно залитый part. */
function saveState(state: ResumeState): void {
  try {
    localStorage.setItem(
      STORAGE_PREFIX + state.recording_id,
      JSON.stringify(state),
    );
  } catch {
    // QuotaExceeded — игнорируем (resume не критичен).
  }
}

function clearState(recordingId: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + recordingId);
  } catch {
    // ignore
  }
}

/** Найти все незавершённые upload'ы в localStorage. */
export function listResumableUploads(): ResumeState[] {
  const out: ResumeState[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as ResumeState;
      if (parsed.recording_id && parsed.filename) {
        out.push(parsed);
      }
    } catch {
      // битый JSON — удаляем
      localStorage.removeItem(key);
    }
  }
  return out.sort((a, b) => b.started_at - a.started_at);
}

/** Удалить state — вызывать после успешного complete / abort. */
export function dropResumeState(recordingId: string): void {
  clearState(recordingId);
}

export interface UploaderCallbacks {
  /** progress: 0..1 (fraction). Вычисляется как (uploaded_bytes / total_bytes). */
  onProgress?: (fraction: number, uploadedBytes: number) => void;
  /** Вызывается на каждый успешно завершённый part. */
  onPartComplete?: (partNumber: number, totalParts: number) => void;
}

export class UploadCancelled extends Error {
  constructor() {
    super("upload cancelled");
    this.name = "UploadCancelled";
  }
}

/**
 * Главный API класса:
 *
 *   const u = new MultipartUploader(file, { onProgress });
 *   await u.start();           // запускает init + part-upload + complete
 *   // или для resume:
 *   const u = MultipartUploader.fromResume(state, file, { onProgress });
 *   await u.start();
 *
 *   u.cancel();                // отменяет: abort'ит на бэке, чистит localStorage
 */
export class MultipartUploader {
  private file: File;
  private callbacks: UploaderCallbacks;
  private state: ResumeState | null = null;
  private initResp: UploadInitResponse | null = null;
  private cancelled = false;
  /** Профиль AI-саммари для нового upload'а (sales/tech/meetup/interview).
   *  Используется только при свежем init; при resume запись уже создана. */
  private summaryProfile: string | undefined;
  private visibility: RecordingVisibility | undefined;
  private tags: { id: string; version: number }[];
  /** uploaded bytes per part number (для progress aggregation). */
  private partProgress = new Map<number, number>();

  constructor(
    file: File,
    callbacks: UploaderCallbacks = {},
    summaryProfile?: string,
    visibility?: RecordingVisibility,
    tags: { id: string; version: number }[] = [],
  ) {
    this.file = file;
    this.callbacks = callbacks;
    this.summaryProfile = summaryProfile;
    this.visibility = visibility;
    this.tags = tags;
  }

  /** Восстановить upload из сохранённого state — юзер заново выбрал тот же файл. */
  static fromResume(
    state: ResumeState,
    file: File,
    callbacks: UploaderCallbacks = {},
  ): MultipartUploader {
    if (file.size !== state.size_bytes || file.name !== state.filename) {
      throw new Error(
        `файл не совпадает с незавершённой загрузкой: ожидалось "${state.filename}" (${state.size_bytes} B), выбрано "${file.name}" (${file.size} B)`,
      );
    }
    // lastModified может расходиться на ms из-за переименований / копирований —
    // мягкая проверка с warn'ом, не fatal.
    if (state.file_last_modified !== file.lastModified) {
      // eslint-disable-next-line no-console
      console.warn(
        "[upload] file.lastModified mismatch (continuing anyway):",
        state.file_last_modified,
        "vs",
        file.lastModified,
      );
    }
    const u = new MultipartUploader(file, callbacks);
    u.state = state;
    return u;
  }

  /** Текущий recording_id (доступен сразу после init либо из resume state). */
  get recordingId(): string | null {
    return this.state?.recording_id ?? null;
  }

  cancel(): void {
    this.cancelled = true;
  }

  async start(): Promise<string> {
    if (this.state === null) {
      // Свежий upload — init на бэке.
      this.initResp = await initUpload(
        this.file.name,
        this.file.size,
        this.file.type || "application/octet-stream",
        this.summaryProfile,
        this.visibility,
      );
      this.state = {
        recording_id: this.initResp.recording_id,
        filename: this.file.name,
        size_bytes: this.file.size,
        part_size: this.initResp.part_size,
        file_last_modified: this.file.lastModified,
        completed_parts: [],
        started_at: Date.now(),
        pending_tags: [...this.tags],
      };
      saveState(this.state);
    } else {
      // Resume — initResp не нужен, всё уже в state.
    }

    while (this.state.pending_tags?.length) {
      const tag = this.state.pending_tags[0];
      await linkTag(this.state.recording_id, tag.id, true, tag.version);
      this.state.pending_tags.shift();
      saveState(this.state);
    }

    const totalParts = Math.max(
      1,
      Math.ceil(this.file.size / this.state.part_size),
    );

    // Множество уже завершённых part_number — пропускаем.
    const done = new Set(
      this.state.completed_parts.map((p) => p.part_number),
    );
    // Уже залитые байты — для прогресса.
    for (let i = 1; i <= totalParts; i++) {
      if (done.has(i)) {
        const partBytes = this.partSizeForIndex(i, totalParts);
        this.partProgress.set(i, partBytes);
      }
    }
    this.emitProgress();

    // Параллельный пул: workerLoop забирает следующий part из общей очереди.
    const queue: number[] = [];
    for (let i = 1; i <= totalParts; i++) {
      if (!done.has(i)) queue.push(i);
    }
    let queueIdx = 0;
    const errors: unknown[] = [];

    const workerLoop = async (): Promise<void> => {
      while (true) {
        if (this.cancelled) return;
        if (errors.length > 0) return;
        const myIdx = queueIdx++;
        if (myIdx >= queue.length) return;
        const partNumber = queue[myIdx];
        try {
          const etag = await this.uploadPart(partNumber);
          if (this.cancelled) return;
          // Кладём в state с сохранением сортировки по part_number.
          this.state!.completed_parts.push({ part_number: partNumber, etag });
          this.state!.completed_parts.sort(
            (a, b) => a.part_number - b.part_number,
          );
          saveState(this.state!);
          this.callbacks.onPartComplete?.(partNumber, totalParts);
        } catch (e) {
          errors.push(e);
          return;
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(PARALLELISM, queue.length) }, () =>
        workerLoop(),
      ),
    );

    if (this.cancelled) {
      await this.handleAbort();
      throw new UploadCancelled();
    }
    if (errors.length > 0) {
      throw errors[0];
    }

    // Финализация на бэке.
    await completeUpload(this.state.recording_id, this.state.completed_parts);
    clearState(this.state.recording_id);
    return this.state.recording_id;
  }

  /** Размер части по индексу (1-based). Последняя часть может быть меньше. */
  private partSizeForIndex(partNumber: number, totalParts: number): number {
    if (partNumber < totalParts) return this.state!.part_size;
    // Последняя — остаток.
    const before = (totalParts - 1) * this.state!.part_size;
    return this.file.size - before;
  }

  private emitProgress(): void {
    let uploaded = 0;
    for (const bytes of this.partProgress.values()) uploaded += bytes;
    this.callbacks.onProgress?.(
      Math.min(1, uploaded / Math.max(1, this.file.size)),
      uploaded,
    );
  }

  private async uploadPart(partNumber: number): Promise<string> {
    const partSize = this.state!.part_size;
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, this.file.size);
    const blob = this.file.slice(start, end);

    // Один retry на сетевые ошибки — multipart parts не дороги перезалить.
    let lastErr: unknown = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (this.cancelled) throw new UploadCancelled();
      try {
        const url = apiURL(`/v1/uploads/${encodeURIComponent(this.state!.recording_id)}/parts/${partNumber}`);
        const etag = await this.putPart(url, blob, partNumber, await csrfHeaders());
        return etag;
      } catch (e) {
        lastErr = e;
        // eslint-disable-next-line no-console
        console.warn(
          `[upload] part ${partNumber} attempt ${attempt + 1} failed:`,
          e,
        );
        // Бэк-офф перед retry.
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    throw lastErr;
  }

  private putPart(url: string, blob: Blob, partNumber: number, headers: Record<string, string>): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      for (const [key,value] of Object.entries(headers)) xhr.setRequestHeader(key,value);
      xhr.setRequestHeader("X-Recordings-Activity", "1");
      uploadActivity(true);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          this.partProgress.set(partNumber, e.loaded);
          this.emitProgress();
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // ETag header — обычно в кавычках, S3 хочет его обратно as-is.
          const etag = xhr.getResponseHeader("ETag") || "";
          if (!etag) {
            reject(new Error(`part ${partNumber}: empty ETag`));
            return;
          }
          // Финальная progress-точка для части — на случай если onprogress
          // не успел добежать до 100%.
          this.partProgress.set(partNumber, blob.size);
          this.emitProgress();
          resolve(etag);
        } else {
          void processAuthError(new Response(xhr.responseText, { status: xhr.status }));
          reject(
            new Error(
              `part ${partNumber}: upload returned ${xhr.status} ${xhr.statusText}`,
            ),
          );
        }
      };
      xhr.onerror = () => reject(new Error(`part ${partNumber}: network error`));
      xhr.onabort = () => reject(new UploadCancelled());
      // На случай ручной отмены пробросим в xhr через флаг.
      const cancelCheck = setInterval(() => {
        if (this.cancelled) {
          clearInterval(cancelCheck);
          xhr.abort();
        }
      }, 500);
      xhr.onloadend = () => { clearInterval(cancelCheck); uploadActivity(false); };
      xhr.send(blob);
    });
  }

  private async handleAbort(): Promise<void> {
    if (!this.state) return;
    try {
      await abortUpload(this.state.recording_id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[upload] abort failed (continuing):", e);
    }
    clearState(this.state.recording_id);
  }
}

/** Прямой abort из state (когда юзер нажал "Отменить" на resume-баннере). */
export async function abortFromState(state: ResumeState): Promise<void> {
  try {
    await abortUpload(state.recording_id);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[upload] abort failed (continuing):", e);
  }
  clearState(state.recording_id);
}
